/**
 * E1. Лотерея. Билет = 0,1 % ВВП на душу (ППС). Джекпот ×2000 билетов с
 * p = 1 : 14 000 000; ×10 с p = 1 : 100; возврат билета с p = 1 : 10.
 * Доля возврата ≈ 0,20. Тяга +1, счастье +1 («надежда»).
 * Возврат билета (×1) пишется как `minigame.lottery.refund`. Без денег на билет — `noPlay()`.
 * Доступна с 18 лет везде, кроме illegal.
 */
import type { Dataset, GamblingLaw } from '@life/data';
import type { Life, MinigameOutcome, Rng } from '@life/engine';
import type { MinigameDef } from '../types';
import { CASINO_MIN_AGE, clampBet, expectancy, gdpPpp, noPlay } from './common';

export interface LotteryState {
  ticketPrice: number;
  /** Джекпот в деньгах = ticketPrice × LOTTERY_JACKPOT_MULT. */
  jackpot: number;
}

export interface LotteryInput {
  scratch: true;
}

export const LOTTERY_TICKET_SHARE = 0.001;
export const LOTTERY_JACKPOT_MULT = 2000;
export const LOTTERY_JACKPOT_P = 1 / 14_000_000;
export const LOTTERY_X10_P = 1 / 100;
export const LOTTERY_X1_P = 1 / 10;
/** Ожидаемый возврат на билет. */
export const LOTTERY_RTP = LOTTERY_JACKPOT_MULT * LOTTERY_JACKPOT_P + 10 * LOTTERY_X10_P + 1 * LOTTERY_X1_P;

/** Множитель выигрыша по одному жребию. */
export function lotteryDraw(rng: Rng): number {
  const r = rng.next();
  if (r < LOTTERY_JACKPOT_P) return LOTTERY_JACKPOT_MULT;
  if (r < LOTTERY_JACKPOT_P + LOTTERY_X10_P) return 10;
  if (r < LOTTERY_JACKPOT_P + LOTTERY_X10_P + LOTTERY_X1_P) return 1;
  return 0;
}

export const lottery: MinigameDef<LotteryInput, LotteryState> = {
  id: 'lottery',
  section: 'E',
  triggers: [{ kind: 'envelope' }],
  available(life: Life, _ds: Dataset, law: GamblingLaw): boolean {
    return life.age >= CASINO_MIN_AGE && law !== 'illegal';
  },
  setup(life: Life, ds: Dataset): LotteryState {
    const ticketPrice = LOTTERY_TICKET_SHARE * gdpPpp(ds, life);
    return { ticketPrice, jackpot: ticketPrice * LOTTERY_JACKPOT_MULT };
  },
  play(state: LotteryState, _input: LotteryInput, life: Life, rng: Rng): MinigameOutcome {
    const ticket = clampBet(state.ticketPrice, life.money);
    if (ticket <= 0) return noPlay();
    const mult = lotteryDraw(rng);
    const moneyDelta = ticket * mult - ticket;
    const logKey =
      mult >= LOTTERY_JACKPOT_MULT ? 'minigame.lottery.jackpot' : mult > 1 ? 'minigame.lottery.win' : mult === 1 ? 'minigame.lottery.refund' : 'minigame.lottery.lose';
    return {
      moneyDelta,
      stats: { happiness: 1 },
      urgeDelta: 1,
      logKey,
      logParams: { amount: Math.round(Math.abs(moneyDelta)), mult },
    };
  },
  expected(): MinigameOutcome {
    return noPlay();
  },
  expectancyText: () => expectancy('lottery', LOTTERY_RTP),
};
